/**
 * Shared, extensible registry for the admin firm-cases filter/badges.
 *
 * The case taxonomy has two orthogonal axes: `posture` and `damages_model`.
 * Adding a new value requires only ONE entry in a facet's options array —
 * that is the extensibility criterion.
 */

export interface CaseFacetCase {
  posture?: 'first_party' | 'third_party' | null;
  damages_model?: 'standard_bi' | 'future_closure' | null;
}
export interface CaseFacetOption {
  id: string;
  label: string;
  predicate: (c: CaseFacetCase) => boolean;
}
export interface CaseFacetGroup {
  id: string;
  label: string;
  options: CaseFacetOption[];
}

export const POSTURE_FACET: CaseFacetGroup = {
  id: 'posture',
  label: 'Posture',
  options: [
    { id: 'all', label: 'All', predicate: () => true },
    { id: 'first_party', label: 'First-party', predicate: (c) => c.posture === 'first_party' },
    { id: 'third_party', label: 'Third-party', predicate: (c) => c.posture === 'third_party' },
    { id: 'unspecified', label: 'Unspecified', predicate: (c) => !c.posture },
  ],
};

export const DAMAGES_FACET: CaseFacetGroup = {
  id: 'damages_model',
  label: 'Damages mode',
  options: [
    { id: 'all', label: 'All', predicate: () => true },
    { id: 'standard_bi', label: 'BI', predicate: (c) => (c.damages_model ?? 'standard_bi') === 'standard_bi' },
    { id: 'future_closure', label: 'Future-closure', predicate: (c) => c.damages_model === 'future_closure' },
  ],
};

export const CASE_FACETS: CaseFacetGroup[] = [POSTURE_FACET, DAMAGES_FACET];

// Badge-label helpers (reused by the per-row badges). Derive from the same vocabulary.
export const postureBadgeLabel = (p?: string | null): string => {
  if (p === 'first_party') return 'First-party';
  if (p === 'third_party') return 'Third-party';
  return 'Unspecified';
};
export const damagesBadgeLabel = (d?: string | null): string => {
  return d === 'future_closure' ? 'Future-closure' : 'BI';
};
